import { Button, Card, CardBody, CardDescription, CardHeader, CardTitle, TextField } from "@community-project-org/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import type { components } from "../../generated/api.js";

type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];

/**
 * Gruppen anlegen und löschen. Sie ordnen das Angebot fürs Team: eine Rolle lässt sich
 * für „Sommerfestival" freigeben statt für jedes Konzert einzeln.
 */
export function ServiceGroupsPanel({ groups }: { groups: ServiceGroupDTO[] }): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["service-groups"] }),
      queryClient.invalidateQueries({ queryKey: ["services"] }),
    ]);

  const create = useMutation({
    mutationFn: () => apiRequest<ServiceGroupDTO>("/service-groups", { method: "POST", body: { name } }),
    onSuccess: async () => {
      setName("");
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/service-groups/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const error = create.error ?? remove.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gruppen</CardTitle>
      </CardHeader>
      <CardDescription>Für Rollenfreigaben, etwa „Einlass nur für das Festival".</CardDescription>
      <CardBody>
        <ul className="grid gap-1">
          {groups.map((group) => (
            <li key={group.id} className="flex min-h-[44px] items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[14px] font-bold text-ink">{group.name}</span>
              <span className="num text-[12.5px] text-ink-3">{group.services} Angebote</span>
              <Button
                variant="ghost"
                aria-label={`Gruppe ${group.name} löschen`}
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Gruppe „${group.name}" löschen? Die Angebote bleiben, Freigaben auf die Gruppe entfallen.`)) {
                    remove.mutate(group.id);
                  }
                }}
              >
                <Trash2 size={16} aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
        <form
          className="mt-3 flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <TextField
            label="Neue Gruppe"
            className="flex-1"
            value={name}
            required
            onChange={(event) => setName(event.target.value)}
          />
          <Button type="submit" disabled={!name.trim() || create.isPending} aria-busy={create.isPending}>
            Anlegen
          </Button>
        </form>
        {error && (
          <p className="mt-2 text-[13px] text-danger" role="alert">
            {error instanceof ApiRequestError ? error.message : "Das hat nicht geklappt."}
          </p>
        )}
      </CardBody>
    </Card>
  );
}
